import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  ArrowRight,
  Clock,
  DollarSign,
  Shield,
  Target,
  Sparkles,
  AlertCircle,
  Zap,
  Calendar,
  MessageSquare,
  TrendingUp
} from 'lucide-react';

const comparisonData = [
  { feature: 'Available 24/7 — no scheduling', vzmo: true, strawberry: false },
  { feature: 'Under $10/month', vzmo: true, strawberry: false },
  { feature: 'Responds instantly', vzmo: true, strawberry: false },
  { feature: 'Remembers every conversation', vzmo: true, strawberry: true },
  { feature: 'Daily check-ins and accountability', vzmo: true, strawberry: false },
  { feature: 'Private journaling built-in', vzmo: true, strawberry: false },
  { feature: 'Goal tracking over time', vzmo: true, strawberry: false },
  { feature: 'Personalized action plans', vzmo: true, strawberry: false },
  { feature: 'Human coach relationship', vzmo: false, strawberry: true },
  { feature: 'ICF-accredited coaches', vzmo: false, strawberry: true },
  { feature: 'Cancel anytime', vzmo: true, strawberry: true },
  { feature: 'No waitlist or matching delay', vzmo: true, strawberry: false },
];

const testimonials = [
  {
    quote: "I looked at Strawberry.me but the cost didn't make sense for what I needed — I just wanted someone to help me think through career decisions every week. VZMO does that for $9.",
    author: "Tyler B.",
    role: "Product Manager",
    note: "Uses VZMO for career clarity"
  },
  {
    quote: "The thing that sold me on VZMO was that I could use it at 11pm on a Tuesday when I was spiraling about a work decision. No coach is available then.",
    author: "Amara J.",
    role: "Nurse Practitioner",
    note: "Uses VZMO for work stress"
  },
  {
    quote: "I actually still have a human coach for deep stuff. But between sessions I use VZMO every day to stay on track. The two work great together.",
    author: "Kevin S.",
    role: "Entrepreneur",
    note: "Uses both human + AI coaching"
  }
];

const useCases = [
  {
    icon: Target,
    title: "Daily accountability",
    description: "Check in every day, not once a week. Coaching works best when it's consistent — not limited to a 45-minute slot."
  },
  {
    icon: MessageSquare,
    title: "Real-time decision support",
    description: "Career crossroads, relationship friction, a hard conversation you're avoiding. Get help when the moment actually happens."
  },
  {
    icon: TrendingUp,
    title: "Progress you can see",
    description: "Journal entries, goals, and action plans build up over time. Your coach remembers everything and tracks your patterns."
  },
  {
    icon: Shield,
    title: "Private by default",
    description: "No profile matching. No data shared. No coach reading your journal. Your conversations stay with you."
  }
];

export function StrawberryMeAlternativePage() {
  useEffect(() => {
    document.title = 'Strawberry.me Alternative — AI Coaching for $9/month | VZMO';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Looking for a Strawberry.me alternative? VZMO offers AI life coaching at $9/month — no scheduling, no waitlist, available 24/7. Get personalized coaching for career, relationships, and personal growth.'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/compare/strawberry-me-alternative');
    }

    // FAQ JSON-LD structured data for Google rich results
    const existingScript = document.getElementById('faq-jsonld-strawberry');
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.id = 'faq-jsonld-strawberry';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is VZMO as good as a human coach from Strawberry.me?',
          acceptedAnswer: { '@type': 'Answer', text: 'Different, not worse. A human coach brings lived experience, nuance, and a real relationship. VZMO brings instant 24/7 availability, daily accountability, perfect memory of your history, and a $9/month cost vs $160+/month for human coaching. Many people use both.' }
        },
        {
          '@type': 'Question',
          name: 'Can I use VZMO alongside a Strawberry.me coach?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes — this is a great combination. Use your Strawberry.me coach for deep sessions. Use VZMO in between to journal, track goals, process decisions as they happen, and show up to sessions with more clarity.' }
        },
        {
          '@type': 'Question',
          name: 'Why is VZMO so much cheaper than Strawberry.me coaches?',
          acceptedAnswer: { '@type': 'Answer', text: 'Strawberry.me coaches are human professionals who charge for their time, typically $80–$250/session. VZMO uses AI to deliver coaching at scale for $9/month unlimited.' }
        },
        {
          '@type': 'Question',
          name: 'Does VZMO remember previous conversations like a human coach would?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. VZMO builds context across every journal entry and coaching session. When you return weeks later, it remembers your goals, what you committed to, and what has changed.' }
        },
        {
          '@type': 'Question',
          name: 'Is there a free VZMO option before committing?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. The free plan includes 3 coaching sessions per week and 10 journal entries per month — no credit card needed.' }
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('faq-jsonld-strawberry');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="bg-white pt-20">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="inline-block text-coral-400 font-medium text-sm tracking-wide uppercase mb-4">
              Strawberry.me Alternative
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Human coaching costs $200/month.
              <span className="block text-coral-400">VZMO costs $9.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8">
              Strawberry.me matches you with a vetted human coach — which is great when you can afford it.
              VZMO is the coach, available instantly, every day, at a price that doesn't require a budget meeting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
              >
                Try VZMO free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#comparison"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                See the comparison
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: Clock, label: 'Available 24/7', desc: 'No booking, no waiting' },
              { icon: DollarSign, label: 'From $9/month', desc: '96% less than human coaching' },
              { icon: Zap, label: 'Instant responses', desc: 'No 24hr email turnaround' }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaching vs therapy disclaimer */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Both VZMO and Strawberry.me are coaching, not therapy</h3>
              <p className="text-amber-800 leading-relaxed">
                Neither platform is a replacement for licensed mental health treatment. If you're experiencing
                a mental health crisis, depression, trauma, or thoughts of self-harm, please reach out to a
                licensed professional. Coaching is for personal growth and goal achievement, not clinical care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The real difference: availability */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              The gap no human coach can fill
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Your hardest moments don't wait for your next session.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Human coaching limitation */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-slate-500" />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-700">Human coaching (Strawberry.me)</h3>
              </div>
              <ul className="space-y-3 text-slate-600">
                {[
                  'Schedule a session in advance',
                  'Wait for a match (up to 24 hours)',
                  'Pay $80–$250 per session',
                  'Between sessions, you\'re on your own',
                  'Can\'t reach your coach at 11pm when you\'re spiraling'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* VZMO advantage */}
            <div className="bg-slate-900 rounded-2xl p-8 border border-coral-500/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-coral-500/20 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-coral-400" />
                </div>
                <h3 className="text-xl font-heading font-bold text-white">VZMO AI coaching</h3>
              </div>
              <ul className="space-y-3 text-slate-300">
                {[
                  'Available instantly, any time of day',
                  'No matching, no waitlist — start now',
                  '$9/month for unlimited sessions',
                  'Checks in on your goals daily',
                  'Remembers everything across every conversation'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Use cases */}
          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center mb-5">
                  <useCase.icon className="h-6 w-6 text-coral-600" />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-3">{useCase.title}</h3>
                <p className="text-slate-600 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost comparison */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <DollarSign className="h-12 w-12 text-coral-500 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              The cost comparison
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Strawberry.me connects you with coaches who charge $80–$250 per session.
              VZMO is $9/month for unlimited access.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <h3 className="font-heading font-bold text-slate-900 text-xl mb-4">Strawberry.me (human coach)</h3>
              <div className="text-4xl font-bold text-slate-900 mb-2">
                $160<span className="text-lg font-normal text-slate-500">/mo avg</span>
              </div>
              <p className="text-sm text-slate-500 mb-6">(2 sessions/month at ~$80/session)</p>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                  <span>Real human relationship</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                  <span>ICF-accredited coaches</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Requires scheduling ahead of time</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>No support between sessions</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Cost scales with usage</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 border border-coral-500/30">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-heading font-bold text-white text-xl">VZMO</h3>
                <span className="px-2 py-0.5 bg-coral-500 text-white text-xs font-medium rounded-full">94% less</span>
              </div>
              <div className="text-4xl font-bold text-white mb-2">
                $9<span className="text-lg font-normal text-slate-400">/mo</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">Unlimited sessions — not per session</p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>AI coach available 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Unlimited coaching sessions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Daily journaling and goal tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Remembers your full history</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Start free — no credit card needed</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">
            *Strawberry.me coach pricing based on publicly available session rates. Actual costs vary by coach.
            VZMO is not a direct replacement — human coaching offers things AI cannot.
          </p>
        </div>
      </section>

      {/* Feature comparison table */}
      <section id="comparison" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Side-by-side comparison
            </h2>
            <p className="text-lg text-slate-600">Different models for different needs</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-4 px-6 font-semibold text-slate-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-coral-600">VZMO</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-500">Strawberry.me</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="py-4 px-6 text-slate-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {row.vzmo ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.strawberry ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Real experiences
            </h2>
            <p className="text-lg text-slate-600">
              From people who evaluated both options
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Sparkles key={i} className="h-4 w-4 text-coral-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                  <p className="text-xs text-coral-600 mt-1">{testimonial.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Common questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "Is VZMO as good as a human coach from Strawberry.me?",
                a: "Different, not worse. A human coach brings lived experience, nuance, and a real relationship — that has genuine value. VZMO brings instant availability, daily accountability, perfect memory of your history, and a cost that means you can actually use it every day instead of once a week. Many people use both."
              },
              {
                q: "Can I use VZMO alongside a Strawberry.me coach?",
                a: "Yes — this is actually a great combination. Use your Strawberry.me coach for deep sessions. Use VZMO in between to journal, track goals, process decisions as they happen, and show up to sessions with more clarity."
              },
              {
                q: "Why is VZMO so much cheaper?",
                a: "Strawberry.me coaches are human professionals who need to charge for their time. VZMO uses AI to deliver coaching at scale. Different cost structures, different pricing — but for daily use and habit-building, AI coaching is hard to beat on value."
              },
              {
                q: "Does VZMO remember what I've talked about before?",
                a: "Yes. Your coach builds context across every journal entry and every coaching session. When you come back three weeks later, it knows what you were working on, what you said you'd do, and what's changed."
              },
              {
                q: "Is there a free option?",
                a: "Yes. The free plan includes 3 coaching sessions per week and 10 journal entries per month — more than enough to get a real feel for it. No credit card needed."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Coaching every day, not just once a week
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Start free. No credit card. No scheduling. Your coach is ready right now.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Start coaching for free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <p className="text-slate-400 text-sm mt-6">
            Not a replacement for human coaching when you need that depth. A complement to it.
          </p>
        </div>
      </section>
    </div>
  );
}
